import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import SEO from '../components/SEO';

const FREE_FEATURES = [
  '5 questions per day',
  'Access to all 7 patterns',
  'Guided meditation timer',
  'Newsletter updates',
];

const PREMIUM_FEATURES = [
  'Everything in Free',
  'Unlimited questions',
  'Longer messages (5,000 chars)',
  'Multiple saved conversations',
  'Auto-named chat history',
  'Support the project',
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    if (!user) {
      navigate('/register', { state: { from: '/pricing' } });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { url } = await api.createCheckoutSession();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user?.tier === 'premium';

  return (
    <div className="min-h-screen pt-24 px-6 pb-16">
      <SEO path="/pricing" />
      <div className="max-w-3xl mx-auto">
        <h1 className="font-serif text-3xl text-warm text-center mb-3">Choose Your Path</h1>
        <p className="text-warm-100/50 text-sm text-center mb-12 max-w-md mx-auto">
          Everyone deserves access to wisdom. Free users can ask 5 questions a day. Premium unlocks unlimited guidance.
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mb-8 text-red-400 text-sm text-center max-w-sm mx-auto">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Tier */}
          <div className="border border-dark-200/50 rounded-xl p-6">
            <h2 className="font-serif text-lg text-warm mb-1">Free</h2>
            <p className="text-warm-100/50 text-xs mb-4">Begin exploring</p>
            <div className="text-3xl font-serif text-warm mb-6">
              $0<span className="text-sm text-warm-100/40 font-sans"> USD/month</span>
            </div>
            <ul className="space-y-3 mb-6">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2 text-warm-100/60 text-sm">
                  <Check size={14} className="text-warm-100/40 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            {!user && (
              <button
                onClick={() => navigate('/register')}
                className="w-full border border-dark-300/50 text-warm-100/60 py-2.5 rounded-lg text-sm hover:border-dark-300 transition-colors"
              >
                Get Started
              </button>
            )}
          </div>

          {/* Premium Tier */}
          <div className="border border-gold/30 rounded-xl p-6 relative">
            <div className="absolute -top-3 left-6 bg-dark text-gold text-xs px-3 py-1 rounded-full border border-gold/30">
              Recommended
            </div>
            <h2 className="font-serif text-lg text-gold mb-1">Premium</h2>
            <p className="text-warm-100/50 text-xs mb-4">Unlimited wisdom</p>
            <div className="text-3xl font-serif text-warm mb-6">
              $7.99<span className="text-sm text-warm-100/40 font-sans"> USD/month</span>
            </div>
            <ul className="space-y-3 mb-6">
              {PREMIUM_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2 text-warm-100/80 text-sm">
                  <Check size={14} className="text-gold mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            {isPremium ? (
              <div className="w-full text-center text-gold/70 py-2.5 text-sm">
                You're on Premium
              </div>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full bg-gold/15 border border-gold/30 text-gold py-2.5 rounded-lg text-sm hover:bg-gold/25 transition-colors disabled:opacity-50"
              >
                {loading ? 'Redirecting...' : user ? 'Upgrade Now' : 'Sign Up & Subscribe'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
